import { hasLabel, useT, type MessageKey } from '../i18n'

interface Props {
  /** Libellé déjà prêt (nom de marque, valeur saisie…). */
  label?: string
  /** Clé de catalogue, quand le libellé est un terme de l'interface. */
  labelKey?: MessageKey
  variant: 'ok' | 'warning' | 'critical' | 'info' | 'neutral'
  /** Valeur technique, affichée en survol quand le libellé est traduit. */
  title?: string
}

const CLASSES: Record<string, string> = {
  ok:       'pill pill-ok',
  warning:  'pill pill-warning',
  critical: 'pill pill-critical',
  info:     'pill pill-info',
  neutral:  'pill pill-neutral',
}

/**
 * La traduction se fait ici, et non chez l'appelant : les fabriques ci-dessous
 * sont de simples fonctions, appelées en plein JSX, qui ne peuvent pas lire le
 * contexte. En laissant la pastille consommer la langue, elle se réaffiche
 * d'elle-même quand l'utilisateur en change.
 */
export function Pill({ label, labelKey, variant, title }: Props) {
  const t = useT()
  return (
    <span className={CLASSES[variant] ?? CLASSES.neutral} title={title}>
      {labelKey ? t(labelKey) : label}
    </span>
  )
}

/**
 * Libellés des états.
 *
 * Les valeurs techniques (OK, PASS, PROHIBITED…) sont le vocabulaire du moteur,
 * pas celui d'un agent de conformité. On les traduit à l'affichage et on garde
 * la valeur d'origine en info-bulle, pour qui doit la rapprocher d'un log.
 */
function pill(
  prefix: string,
  value: string,
  variant: Props['variant'],
) {
  // Un code que le serveur ajoute avant l'interface s'affiche brut plutôt que
  // de faire disparaître l'information.
  return hasLabel(prefix, value) ? (
    <Pill labelKey={`${prefix}.${value}` as MessageKey} variant={variant} title={value} />
  ) : (
    <Pill label={value} variant={variant} title={value} />
  )
}

export function riskPill(level: string) {
  const v = level === 'OK' ? 'ok' : level === 'WARNING' ? 'warning' : 'critical'
  return pill('risk', level, v)
}

export function actionPill(action: string) {
  const map: Record<string, Props['variant']> = {
    PASS: 'ok', REVIEW: 'info', SUSPEND: 'warning', BLOCK: 'critical', TAKEDOWN: 'critical',
  }
  return pill('action', action, map[action] ?? 'neutral')
}

export function suspicionPill(level: string) {
  const map: Record<string, Props['variant']> = {
    LOW: 'ok', MEDIUM: 'info', HIGH: 'warning', CRITICAL: 'critical',
  }
  return pill('suspicion', level, map[level] ?? 'neutral')
}

export function rulePill(level: string) {
  const map: Record<string, Props['variant']> = {
    ALLOWED: 'ok', CONDITIONAL: 'info', FLAGGED: 'warning', PROHIBITED: 'critical', 'N/A': 'neutral',
  }
  // « N/A » ne fait pas une clé de catalogue lisible : la valeur porte le point.
  const key = level === 'N/A' ? 'NA' : level
  return pill('rule', key, map[level] ?? 'neutral')
}
